import { Text } from "@ui-kitten/components";
import MainContainer from "components/MainContainer";
import Spacer from "components/Spacer";

import { InvoiceDetails } from "features/types";
import { useAppSelector } from "hooks/useAppSelector";
import { PaymentMethodScreenProps } from "navigation/types";
import React, { useCallback } from "react";
import { Platform, View } from "react-native";
import BaseCard, { BaseCardListItem } from "components/BaseCard";
import PaymentMethodCard from "screens/Account/components/PaymentMethodCard";
import Button from "components/Button";
import * as Print from "expo-print";
import { shareAsync } from "expo-sharing";
import generateReceiptHtml from "./generateReceiptHtml";
import { useFetchInvoices } from "queries/account.queries";
import { useAccount } from "hooks/useAccount";
import i18n from "plugins/localization";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import { useFetchTenantBrandingQuery } from "queries/tenant.queries";
import { formatCurrency } from "utils/appUtils";

const RenderInvoice: React.FC<{ invoice: InvoiceDetails }> = ({ invoice }) => (
  <View>
    <Text>Billing period: {invoice.billingPeriod}</Text>
    <Text>Charges: {formatCurrency(invoice.charges)}</Text>
  </View>
);

const PaymentMethod = ({ navigation }: PaymentMethodScreenProps) => {
  const { account } = useAccount();
  const { data: invoices } = useFetchInvoices();
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();

  const { tenantType } = useAppSelector((state) => state.tenant);
  const { data: branding } = useFetchTenantBrandingQuery();

  const handleDownloadInvoice = useCallback(
    async (invoice: InvoiceDetails) => {
      if (!tenantConfig || !tenantType || !branding) return;

      const html = generateReceiptHtml(invoice, tenantConfig, branding.name);
      // since expo-print is not supported in web, use this hack:
      // https://github.com/expo/expo/issues/10553#issuecomment-748599466
      if (Platform.OS === "web") {
        const pW = window.open("", "", "height=800, width=600")!;
        pW.document.write(html);
      } else {
        const { uri } = await Print.printToFileAsync({ html });
        await shareAsync(uri, { UTI: ".pdf", mimeType: "application/pdf" });
      }
    },
    [tenantConfig, tenantType, branding]
  );

  if (!account) return null;

  return (
    <MainContainer
      title="Payment Method"
      goBackAction={() => {
        navigation.navigate("AccountScreen");
      }}
      grayBackground
    >
      <View style={{ padding: 15 }}>
        <PaymentMethodCard />

        <Spacer />
        <BaseCard title="Payment Interval">
          <BaseCardListItem
            title="Change Payment Interval"
            onPress={() =>
              navigation.navigate("ManageSubscriptions", {
                screen: "ManageSubscriptionsScreen",
              })
            }
          ></BaseCardListItem>
        </BaseCard>

        <Spacer />
        <BaseCard title="Transaction History">
          {invoices === null && (
            <BaseCardListItem title={i18n.t("common.loading")} />
          )}
          {invoices?.map((invoice, index) => (
            <BaseCardListItem
              testID="invoice-item"
              key={index}
              title={<RenderInvoice invoice={invoice} />}
              accessoryRight={
                <Button
                  size="small"
                  appearance="ghost"
                  onPress={() => handleDownloadInvoice(invoice)}
                >
                  View Details
                </Button>
              }
            />
          ))}
        </BaseCard>
      </View>
    </MainContainer>
  );
};

export default PaymentMethod;
